import { fetchJson, getConfig } from './client';
import { StoreApiError, type UserProfileInput, type UserProfileResponse } from './types';

/**
 * Reads the caller's Firestore `users/{uid}` profile via HTTPS `GET /userProfile`
 * (Firebase Cloud Function or commerce). Requires bearer token.
 */
export async function fetchUserProfile(): Promise<UserProfileResponse> {
  const { apiBase, userProfileReadBase } = getConfig();
  const baseUrl = userProfileReadBase ?? apiBase;
  const data = await fetchJson<UserProfileResponse>({
    method: 'GET',
    relativePath: '/userProfile',
    errorContext: 'Fetch user profile',
    baseUrl,
    networkErrorMessage: 'Network error. Could not load.',
  });
  if (typeof data !== 'object' || data === null) {
    throw new StoreApiError('invalid-response', 'User profile server returned an unexpected shape.');
  }
  return data;
}

/**
 * Upserts the caller's profile at `users/{uid}` via alchemie-commerce's
 * `userProfile` function. Email is derived server-side from the verified
 * ID token — not trusted from the body.
 *
 * `marketingConsent: true` on first write stamps `marketingConsentAccepted`
 * + `marketingConsentAcceptedAt` on the server; subsequent true values
 * preserve the original acceptance timestamp.
 */
export async function saveUserProfile(input: UserProfileInput): Promise<void> {
  const body: Record<string, unknown> = { name: input.name };
  if (input.institution) {
    body.institution = input.institution;
  }
  if (input.marketingConsent === true) {
    body.marketingConsent = true;
  }

  await fetchJson<{ ok: boolean }>({
    method: 'POST',
    relativePath: '/userProfile',
    body,
    errorContext: 'Saving profile',
  });
}
